/**
 * Human-readable duration. Precise under the hour so short builds read true:
 * a 30 s upgrade must show "30 s", not the rounded-up "1 min" the old
 * `Math.round(s / 60)` produced. The building time curve (base × growth^n) yields
 * non-round values (45 s, 68 s, 101 s…), so sub-hour durations keep their seconds.
 */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s} s`
  if (s < 3600) {
    const m = Math.floor(s / 60)
    const rem = s % 60
    return rem === 0 ? `${m} min` : `${m} min ${rem} s`
  }
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`
}

/**
 * Relative "il y a …" label for a past ISO timestamp (notification feed). `now`
 * is injectable so the formatting stays deterministic in tests. Future or
 * sub-minute timestamps read "à l'instant"; beyond a week we fall back to the
 * compact absolute date (jj/mm) — the project's house style for old dates.
 */
export function formatTimeAgo(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const sec = Math.floor((now - then) / 1000)
  if (sec < 60) return "à l'instant"
  const min = Math.floor(sec / 60)
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  const d = Math.floor(h / 24)
  if (d <= 7) return `il y a ${d} j`
  return new Date(then).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
}
